import { useCelebrationSlot } from "@/lib/celebrationQueue";
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsUp, Star } from "lucide-react";
import { useGradeTier } from "@/hooks/useGradeTier";
import { useKidsDarkMode, KT } from "@/context/KidsDarkModeContext";
import { decideLevelUp, levelAckKey } from "./levelAck";

interface LevelUpOverlayProps {
    level: number;
    tier: string;
    // True while UserProgressContext's real fetch is still in flight — see the
    // matching comment in StreakCelebrationOverlay for why this replaces a
    // fixed hydration timer.
    isLoading: boolean;
    // The signed-in account. The acknowledgement is stored per account so a
    // shared device cannot let one student's level suppress another's.
    userId?: string;
}


export const LevelUpOverlay: React.FC<LevelUpOverlayProps> = ({ level, tier, isLoading, userId }) => {
    const [show, setShow] = useState(false);
    const { visible, onExitComplete } = useCelebrationSlot(show, 1);
    const [announcedLevel, setAnnouncedLevel] = useState(level);
    const [announcedTier, setAnnouncedTier] = useState(tier);
    const hydrated = useRef(false);

    useEffect(() => {
        // Data is still loading — `level` defaults to 1 before the real value
        // arrives, so don't read/write the acknowledgement against it yet.
        if (isLoading || !userId) return;

        const key = levelAckKey(userId);
        const read = (): number => {
            try { return parseInt(localStorage.getItem(key) || '0', 10) || 0; } catch { return 0; }
        };
        const write = (value: number) => {
            try { localStorage.setItem(key, String(value)); } catch { /* Private browsing may block storage. */ }
        };

        const decision = decideLevelUp(level, read(), hydrated.current);
        if (decision === 'record-silently') {
            write(level);
            hydrated.current = true;
            return;
        }

        if (decision === 'celebrate') {
            setAnnouncedLevel(level);
            setAnnouncedTier(tier);
            setShow(true);
            write(level);


        }

        if (decision === 'sync-silently') write(level);
        hydrated.current = true;
    }, [level, tier, isLoading, userId]);

    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(() => setShow(false), 8000);
        return () => clearTimeout(timer);
    }, [visible, announcedLevel]);

    // Accessibility / Modals: dismiss with Escape while shown.
    useEffect(() => {
        if (!visible) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setShow(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [visible]);


    // Move focus into the celebration and give it back afterwards, so a
    // keyboard or screen-reader user isn't left behind the overlay.
    const returnFocus = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (visible) {
            returnFocus.current = document.activeElement as HTMLElement | null;
        } else if (returnFocus.current) {
            returnFocus.current.focus?.();
            returnFocus.current = null;
        }
    }, [visible]);

    const { isKids } = useGradeTier();
    const { isDark: isKidsDark } = useKidsDarkMode();

    // Same Experience-System palette family as the streak/upgrade overlays —
    // one calm ember identity for every celebration moment, theme-aware.
    // Follows the active palette (Paper, Sky, Focus) in light and dark.
    const c = {
        scrim: "color-mix(in srgb, var(--bg-primary) 95%, transparent)",
        ink: "var(--text-primary)", ink2: "var(--text-secondary)", muted: "var(--text-muted)",
        ember: "var(--accent-primary)", onEmber: "var(--accent-on-primary)",
        border: "var(--border-default)",
        track: "color-mix(in srgb, var(--text-primary) 10%, transparent)",
        glow: "color-mix(in srgb, var(--accent-primary) 22%, transparent)",
        spark1: "var(--accent-primary)", spark2: "var(--accent-warning)",
        chip: "color-mix(in srgb, var(--text-primary) 5%, transparent)",
    };

    // The rest of this component is a muted, editorial "ember" celebration
    // (serif type, low-saturation scrim) that reads as somber rather than
    // exciting against Kids Mode's bright Duolingo-style visual language
    // everywhere else — this overlay had zero kids-mode awareness at all.
    // Reuses every bit of the trigger/acknowledgment logic above; only the
    // rendered JSX differs. Found 2026-09-08 reviewing Kids Mode feedback.
    if (isKids) {
        return (
            <AnimatePresence onExitComplete={onExitComplete}>
                {visible && (
                    <motion.div
                        key="kids-levelup-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Level ${announcedLevel} reached`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.4 } }}
                        className="fixed inset-0 z-celebration flex items-center justify-center overflow-hidden px-6"
                        style={{ background: isKidsDark ? "rgba(19,31,46,0.94)" : "rgba(255,248,225,0.96)" }}
                        onClick={() => setShow(false)}
                    >
                        {/* Bursting stars — bright and plentiful, unlike the adult
                            version's sparse ember sparks */}
                        <div className="absolute inset-0 pointer-events-none">
                            {[...Array(16)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute"
                                    style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0.8], rotate: 180 }}
                                    transition={{ duration: 1.8, repeat: Infinity, delay: Math.random() * 2, ease: "easeOut" }}
                                >
                                    <Star
                                        className="fill-current"
                                        style={{ width: 12 + Math.random() * 16, height: 12 + Math.random() * 16, color: ["#FFC800", "#FF9A3C", "#58CC02", "#1CB0F6", "#CE82FF"][i % 5] }}
                                    />
                                </motion.div>
                            ))}
                        </div>

                        <div
                            className="relative z-10 flex flex-col items-center text-center rounded-[36px] p-8 shadow-2xl border-4"
                            style={{
                                background: isKidsDark ? KT.card(true) : "#ffffff",
                                borderColor: "#FFC800",
                                borderBottomWidth: 8,
                                borderBottomColor: "#D9A200",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div
                                initial={{ scale: 0.6, rotate: -8, opacity: 0 }}
                                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }}
                                className="w-28 h-28 rounded-[28px] flex flex-col items-center justify-center shadow-lg"
                                style={{ background: "linear-gradient(160deg,#FFC800,#FF9A3C)", border: "4px solid white" }}
                            >
                                <span className="text-[11px] font-black uppercase tracking-widest text-white/90">Level</span>
                                <span className="text-5xl font-black text-white leading-none">{announcedLevel}</span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                                className="text-2xl font-black mt-6"
                                style={{ color: KT.heading(isKidsDark) }}
                            >
                                You leveled up!
                            </motion.h1>
                            <motion.p
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                                className="text-sm font-bold mt-1"
                                style={{ color: KT.sub(isKidsDark) }}
                            >
                                You're a {announcedTier} now, awesome work!
                            </motion.p>

                            <motion.button
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                                onClick={() => setShow(false)}
                                className="mt-7 h-12 px-8 rounded-full font-black text-white text-sm active:scale-95 transition-all shadow-lg"
                                style={{ background: "#58CC02", borderBottom: "4px solid #46A302" }}
                            >
                                Keep Going!
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence onExitComplete={onExitComplete}>
            {visible && (
                <motion.div
                    key="levelup-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Level ${announcedLevel} reached`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
                    className="fixed inset-0 z-celebration flex items-center justify-center overflow-hidden backdrop-blur-xl px-6"
                    style={{ background: c.scrim }}
                    onClick={() => setShow(false)}
                >
                    {/* No close button — Continue button and auto-dismiss (8s) provide
                        clear paths to acknowledgement without accidental dismissal.
                        Escape key and backdrop-click still available for power users. */}

                    <div
                        className="relative z-10 flex flex-col items-center text-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <motion.div
                            initial={{ scale: 0.7, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 190, damping: 16, delay: 0.1 }}
                            className="relative"
                        >
                            <div
                                className="w-24 h-24 rounded-[28px] flex flex-col items-center justify-center"
                                style={{ background: c.ember, boxShadow: `0 12px 32px -8px ${c.glow}` }}
                            >
                                <span className="text-caption font-semibold" style={{ color: c.onEmber, opacity: 0.85 }}>
                                    Level
                                </span>
                                <span className="font-serif text-4xl leading-none tabular-nums" style={{ color: c.onEmber }}>
                                    {announcedLevel}
                                </span>
                            </div>
                            <motion.div
                                initial={{ y: -6, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.35 }}
                                className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                                style={{ background: c.ember }}
                            >
                                <ChevronsUp className="w-4.5 h-4.5" style={{ color: c.onEmber }} strokeWidth={3} />
                            </motion.div>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35 }}
                            className="font-serif text-title mt-7"
                            style={{ color: c.ink }}
                        >
                            You've levelled up.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                            className="mt-2 text-body" style={{ color: c.ink2 }}
                        >
                            You're now {announcedTier}.
                        </motion.p>

                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.55 }}
                            onClick={() => setShow(false)}
                            autoFocus
                            className="mt-8 h-12 px-8 rounded-xl font-semibold text-body-sm transition-transform hover:-translate-y-[1px] active:translate-y-0"
                            style={{ background: c.ember, color: c.onEmber, boxShadow: `0 10px 24px -10px ${c.glow}` }}
                        >
                            Continue
                        </motion.button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
